package terraultima.web

import terraultima.Fmt
import terraultima.game.*
import kotlin.math.abs
import kotlin.math.roundToInt

/**
 * The readouts: the strip, and the four panels behind the tab bar.
 *
 * The copy decisions here are the same ones the Telegram bot's `Render.kt` argues for — the word
 * leads and the rung follows it, a trend is stated once, an empty price says so out loud — but the
 * medium is different enough that sharing the code would mean sharing neither. A message is a string
 * with eleven legal tags; this is a document with a stylesheet. What is shared is the reasoning, and
 * `docs/11-TELEGRAM.md` holds it.
 */
object Panels {

    fun num(d: Double): String = when {
        d >= 1000 -> Fmt.int(d).reversed().chunked(3).joinToString(",").reversed()
        d >= 10 -> Fmt.int(d)
        d >= 1 -> Fmt.dec(d, 1)
        d > 0 -> Fmt.dec(d, 2)
        else -> "0"
    }

    /** For a chip, where four characters is the whole budget. */
    fun short(d: Double): String = when {
        d >= 10_000 -> Fmt.int(d / 1000) + "k"
        d >= 1_000 -> Fmt.dec(d / 1000, 1) + "k"
        d >= 10 -> Fmt.int(d)
        d > 0 -> Fmt.dec(d, 1)
        else -> "0"
    }

    fun icon(stock: String) = when {
        stock.startsWith("food") -> "🍞"
        stock.startsWith("water") -> "💧"
        stock.startsWith("spares") -> "🔧"
        stock.startsWith("medicine") -> "💊"
        stock.contains("fuel") -> "🚀"
        else -> "📦"
    }

    fun severityClass(s: Severity) = when (s) {
        Severity.CRISIS -> "crisis"
        Severity.OPPORTUNITY -> "opportunity"
        Severity.NOTABLE -> "notable"
        Severity.CALM -> "calm"
    }

    fun severityWord(s: Severity) = when (s) {
        Severity.CRISIS -> "crisis"
        Severity.OPPORTUNITY -> "opportunity"
        Severity.NOTABLE -> "notable"
        Severity.CALM -> "quiet"
    }

    // ------------------------------------------------------------------ the strip

    /**
     * The one row that is never behind a tap: people, the two stores you die without, power, and the
     * mood that is currently worst. It is the answer to "can I afford this option" for most options
     * in the corpus, which is why it sits under the clock instead of inside a panel.
     */
    fun strip(n: N, c: ColonyView, onOpen: () -> Unit) {
        n.div("strip") {
            click(onOpen)
            chip(this, "👥", c.souls.toString(), "", 0, false)
            // EVERY store, not the first two. Spares, medicine and fuel are spent by options and
            // were visible only inside a panel, so "is spares even a resource?" was a fair question.
            for (s in c.stocks)
                chip(this, icon(s.label), s.note.ifEmpty { short(s.amount) }, s.label, s.trend, s.low)
            // "1.6k/656" is two numbers, no units and no order. The word leads; the ratio explains it.
            chip(this, "⚡", c.powerNote, "${short(c.powerDrawn)} of ${short(c.powerMade)} kW", 0, c.powerShort)
            c.gauges.minByOrNull { it.band }?.let {
                chip(this, "", it.word, it.label, 0, it.band == 0)
            }
            span("strip-more") { +"⌃" }
        }
    }

    private fun chip(n: N, icon: String, value: String, note: String, trend: Int, warn: Boolean) {
        n.span("chip" + if (warn) " warn" else "") {
            if (icon.isNotEmpty()) span("chip-icon") { +icon }
            span("chip-value") { +value }
            if (trend != 0) span("chip-trend") { +(if (trend > 0) "↑" else "↓") }
            if (note.isNotEmpty()) span("chip-note") { +note }
        }
    }

    // ------------------------------------------------------------------ panels

    private fun N.meter(band: Int, count: Int) {
        div("meter") {
            for (k in 0 until count) div("pip" + if (k <= band) " on" else "")
        }
    }

    fun colony(n: N, snap: Snapshot, debug: Boolean) {
        val c = snap.colony
        n.apply {
            div("row-head") { +"${c.souls} people" }
            line("row-sub", plural(c.adults, "adult") + " · " +
                    plural(c.children, "child", "children") + " · " + plural(c.elders, "elder") +
                    (if (c.asleep > 0) " · ${c.asleep} asleep" else ""))

            div("panel-label") { +"STORES" }
            for (s in c.stocks) div("readout" + if (s.low) " warn" else "") {
                span("readout-icon") { +icon(s.label) }
                div("readout-body") {
                    div("readout-top") {
                        span("readout-name") { +s.label }
                        span("readout-value") { +s.note.ifEmpty { num(s.amount) } }
                    }
                    // `s.note` is the readout-value above, so repeating it here said everything
                    // twice.
                    val notes = listOfNotNull(
                        s.trendNote.ifEmpty { null },
                        if (s.low) "running out" else null
                    )
                    if (notes.isNotEmpty()) div("readout-note") { +notes.joinToString(" · ") }
                }
            }

            div("readout") {
                span("readout-icon") { +"⚡" }
                div("readout-body") {
                    div("readout-top") {
                        span("readout-name") { +"power" }
                        span("readout-value") {
                            +"${num(c.powerDrawn)} / ${num(c.powerMade)} kW"
                        }
                    }
                    val bits = mutableListOf(c.powerNote)
                    if (c.powerCommitted <= -1.0)
                        bits += "${num(-c.powerCommitted)} kW committed by your decisions"
                    else if (c.powerCommitted >= 1.0) bits += "${num(c.powerCommitted)} kW freed"
                    div("readout-note") { +bits.joinToString(" · ") }
                }
            }

            div("panel-label") { +"MOOD" }
            for (g in c.gauges) div("gauge") {
                div("gauge-top") {
                    span("gauge-word") { +g.word }
                    span("gauge-name") { +g.label }
                }
                meter(g.band, g.bandCount)
            }

            if (c.strains.isNotEmpty()) {
                div("panel-label") { +"STRAIN THE COLONY HAS SHOWN YOU" }
                for (g in c.strains) div("gauge strain") {
                    div("gauge-top") {
                        span("gauge-word") { +g.label }
                        span("gauge-name") { +g.word }
                    }
                    meter(g.band, g.bandCount)
                }
            }

            div("panel-label") { +"THE PLACE" }
            if (c.cultivated > 0) line("note", "${num(c.cultivated)} m² cultivated" +
                    (if (c.shortHanded) " — short-handed, labour at ${c.labourPct}%" else ""))
            line("note", "knowledge ${num(c.surveyData)} of 100 — what the colony understands " +
                    "about its own machines and this world" +
                    (if (c.surveyData >= 100.0) "; it has all the skill this can buy"
                     else "; it raises skill as it accumulates, up to 100"))
            if (c.shelterFor > 0) line("note",
                "the storm shelter holds ${c.shelterFor} of ${c.souls} — about thirty minutes of warning")

            div("panel-label") { +"CAN DO" }
            div("tags") { for (cap in c.capabilities) span("tag") { +cap } }
            if (c.broken.isNotEmpty()) {
                div("panel-label") { +"BROKEN" }
                div("tags") { for (b in c.broken) span("tag bad") { +b } }
            }

            if (debug) {
                div("panel-label") { +"DESIGNER VIEW" }
                div("tags") {
                    for ((k, v) in c.pressures.filter { it.second >= 0.05 }.sortedByDescending { it.second })
                        span("tag mono") { +"$k ${Fmt.dec(v, 2)}" }
                }
                div("tags") {
                    for ((k, v) in c.axes.filter { abs(it.second) >= 0.05 })
                        span("tag mono") { +("$k ${if (v > 0) "+" else ""}${Fmt.dec(v, 2)}") }
                }
                line("note mono", "tension ${Fmt.dec(c.tension, 2)}")
            }
        }
    }

    fun world(n: N, snap: Snapshot) {
        n.apply {
            div("row-head") { +snap.planetName }
            if (snap.facts.isEmpty()) { line("note", "nothing is known yet."); return@apply }
            for (f in snap.facts) div("fact") {
                span("fact-conf " + when (f.confidence) {
                    "measured" -> "measured"; "estimated" -> "estimated"; else -> "unknown"
                }) { +when (f.confidence) { "measured" -> "●"; "estimated" -> "◐"; else -> "○" } }
                div("fact-body") {
                    div("readout-top") {
                        span("readout-name") { +f.label }
                        span("readout-value") { +f.text }
                    }
                    if (f.learnedBy.isNotEmpty() && f.confidence != "measured")
                        div("readout-note") { +"would be settled by ${f.learnedBy}" }
                }
            }
            if (snap.potential.isNotEmpty()) {
                div("panel-label") { +"WHAT THIS PLACE IS GOOD FOR" }
                for (p in snap.potential) line("bullet", p)
            }
        }
    }

    fun aims(n: N, snap: Snapshot) {
        n.apply {
            if (snap.goals.isEmpty()) { line("note", "nothing yet."); return@apply }
            for (g in snap.goals.sortedBy { !it.longTerm }) div("aim" + if (g.done) " done" else "") {
                div("aim-top") {
                    span("aim-kind") { +(if (g.longTerm) "long" else "near") }
                    span("aim-label") { +g.label }
                    span("aim-pct") { +"${(g.progress * 100).roundToInt()}%" }
                }
                div("bar") { div("bar-fill").style.width = "${(g.progress * 100).roundToInt()}%" }
                div("readout-note") { +g.detail }
                if (g.blocked.isNotEmpty()) div("readout-note warn") { +g.blocked }
            }
            line("note", "read from the colony, not set by anyone.")
        }
    }

    fun log(n: N, snap: Snapshot) {
        n.apply {
            if (snap.history.isEmpty()) { line("note", "you have not decided anything yet."); return@apply }
            for (h in snap.history.asReversed()) div("logrow") {
                span("dot " + severityClass(h.severity)) { +"" }
                div("logrow-body") {
                    div("logrow-top") {
                        span("logrow-year") { +"year ${h.year}" }
                        span("logrow-title") { +h.title }
                    }
                    div("logrow-chosen") { +h.chosen }
                }
            }
        }
    }

    fun expedition(n: N, v: SetupView, snap: Snapshot, sectionOpen: (String) -> Boolean,
                   toggle: (String) -> Unit) {
        n.apply {
            div("premise-card") {
                div("panel-label") { +"YOU ARE LEAVING EARTH BECAUSE" }
                div("row-head") { +v.motiveName }
                p("body") { +v.motiveLine }
                p("body dim") { +v.motiveDetail }
            }
            div("facts-grid") {
                factCell(this, "ship", v.shipName, v.shipNote)
                factCell(this, "mission", v.missionLine, v.planetNote)
                factCell(this, "crossing", "${v.crossingYears} years", "")
                factCell(this, "testing", "${v.testingPct}%",
                    "expect ${num(v.expectedFaultsLow)}–${num(v.expectedFaultsHigh)} unplanned faults " +
                            "over a century")
            }
            val short = v.budgets.filter { !it.ok }
            div("verdict" + if (short.isEmpty()) " ok" else " warn") {
                // WHAT THE SHORTFALL DOES, NOT A THREAT. "Short on Spare fuel to choose a site.
                // You die at whichever budget runs out first." was the last thing a first-time
                // player read before Launch, on a sheet they cannot change: it said they would
                // lose, and did not say that what they would actually lose is the choice of where
                // to land.
                +(if (short.isEmpty()) "Nothing aboard is short for the crossing."
                  else short.joinToString(" ") { it.label + ": " + it.consequence + "." })
            }
            section("budgets", "MISSION BUDGETS", sectionOpen, toggle) {
                for (b in v.budgets) div("readout" + if (b.ok) "" else " warn") {
                    span("readout-icon") { +(if (b.ok) "✓" else "!") }
                    div("readout-body") {
                        div("readout-top") {
                            span("readout-name") { +b.label }
                            span("readout-value") { +"${num(b.have)} / ${num(b.need)} ${b.unit}" }
                        }
                        if (!b.ok) div("readout-note") { +b.consequence }
                    }
                }
            }
            section("modules", "WHAT YOU ARE FLYING", sectionOpen, toggle) {
                line("note", "Each module either grants a verb the colony can perform, or quietly " +
                        "changes the physics. Both matter; only the first ever shows up as a choice.")
                for (m in snap.modules) div("module") {
                    div("readout-top") {
                        span("readout-name") { +m.name }
                        span("cond " + when (m.condition) {
                            "ok" -> "ok"; "worn" -> "worn"; else -> "broken"
                        }) { +m.condition }
                    }
                    if (m.grants.isNotEmpty()) div("readout-note") { +("lets you " + m.grants.joinToString(", ")) }
                    if (m.effects.isNotEmpty()) div("readout-note dim") { +("also " + m.effects.joinToString(", ")) }
                }
            }
            section("crew", "WHO IS ABOARD", sectionOpen, toggle) {
                for (c in snap.crewNote) p("body") { +c }
            }
            section("world", "WHAT YOU KNOW ABOUT ${snap.planetName.uppercase()}", sectionOpen, toggle) {
                world(this, snap)
            }
            section("aims", "WHAT YOU WILL BE WORKING TOWARD", sectionOpen, toggle) {
                aims(this, snap)
            }
        }
    }

    private fun factCell(n: N, label: String, value: String, note: String) {
        n.div("fact-cell") {
            div("panel-label") { +label.uppercase() }
            div("fact-value") { +value }
            if (note.isNotEmpty()) div("readout-note") { +note }
        }
    }

    private fun N.section(id: String, title: String, open: (String) -> Boolean,
                          toggle: (String) -> Unit, body: N.() -> Unit) {
        div("section" + if (open(id)) " open" else "") {
            div("section-head") {
                click { toggle(id) }
                span("section-title") { +title }
                span("section-chevron") { +(if (open(id)) "⌃" else "⌄") }
            }
            if (open(id)) div("section-body") { body() }
        }
    }
}
